import {
  email,
  required,
  useRecordContext,
  useTranslate,
  useUpdate,
  useNotify,
} from "ra-core";
import type { FocusEvent, ClipboardEventHandler } from "react";
import { useFormContext } from "react-hook-form";
import { Separator } from "@/components/ui/separator";
import { useIsMobile } from "@/hooks/use-mobile";
import { BooleanInput } from "@/components/admin/boolean-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { TextInput } from "@/components/admin/text-input";
import { DateTimeInput } from "@/components/admin/date-time-input";
import { RadioButtonGroupInput } from "@/components/admin/radio-button-group-input";
import { SelectInput } from "@/components/admin/select-input";
import { AutocompleteArrayInput } from "@/components/admin/autocomplete-array-input";
import { ArrayInput } from "@/components/admin/array-input";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";

import { isLinkedinUrl } from "../misc/isLinkedInUrl";
import { StatusSelector } from "../notes";
import type { Contact } from "../types";
import {
  FIT_CONFIDENCES,
  FIT_QUALITIES,
  ICP_PROFILES,
  RELATIONSHIP_LEVELS,
  RELATIONSHIP_TYPES,
  BUYER_STATUSES,
} from "../root/crmVocabulary.generated";
import { Avatar } from "./Avatar";
import { AutocompleteCompanyInput } from "../companies/AutocompleteCompanyInput.tsx";
import {
  contactGender,
  translateContactGenderLabel,
  translatePersonalInfoTypeLabel,
} from "./contactModel.ts";

export const ContactInputs = () => {
  const isMobile = useIsMobile();

  return (
    <div className="flex flex-col gap-2 p-1 relative md:static">
      <div className="absolute top-0 right-1 md:static">
        <Avatar />
      </div>
      <div className="flex gap-10 md:gap-6 flex-col md:flex-row">
        <div className="flex flex-col gap-10 flex-1">
          <ContactIdentityInputs />
          <ContactPositionInputs />
        </div>
        {isMobile ? null : (
          <Separator orientation="vertical" className="flex-shrink-0" />
        )}
        <div className="flex flex-col gap-10 flex-1">
          <ContactPersonalInformationInputs />
          <ContactLocationInputs />
          <ContactRelationshipInputs />
          <ContactMiscInputs />
        </div>
      </div>
    </div>
  );
};

const ContactIdentityInputs = () => {
  const translate = useTranslate();
  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">
        {translate("resources.contacts.field_categories.identity")}
      </h6>
      <RadioButtonGroupInput
        label={false}
        row
        source="gender"
        choices={contactGender}
        helperText={false}
        optionText={(choice) => translateContactGenderLabel(choice, translate)}
        translateChoice={false}
        optionValue="value"
        defaultValue={contactGender[0].value}
      />
      <TextInput source="first_name" validate={required()} helperText={false} />
      <TextInput source="last_name" validate={required()} helperText={false} />
    </div>
  );
};

const ContactPositionInputs = () => {
  const translate = useTranslate();
  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">
        {translate("resources.contacts.field_categories.position")}
      </h6>
      <TextInput source="title" helperText={false} />
      <ReferenceInput source="company_id" reference="companies" perPage={10}>
        <AutocompleteCompanyInput label="resources.contacts.fields.company_id" />
      </ReferenceInput>
    </div>
  );
};

const ContactPersonalInformationInputs = () => {
  const translate = useTranslate();
  const { getValues, setValue } = useFormContext();
  const personalInfoTypes = [
    {
      id: "Work",
      name: translatePersonalInfoTypeLabel("Work", translate),
    },
    {
      id: "Home",
      name: translatePersonalInfoTypeLabel("Home", translate),
    },
    {
      id: "Other",
      name: translatePersonalInfoTypeLabel("Other", translate),
    },
  ];

  // set first and last name based on email
  const handleEmailChange = (email: string) => {
    const { first_name, last_name } = getValues();
    if (first_name || last_name || !email) return;
    const [first, last] = email.split("@")[0].split(".");
    setValue("first_name", first.charAt(0).toUpperCase() + first.slice(1));
    setValue(
      "last_name",
      last ? last.charAt(0).toUpperCase() + last.slice(1) : "",
    );
  };

  const handleEmailPaste: ClipboardEventHandler<
    HTMLTextAreaElement | HTMLInputElement
  > = (e) => {
    const email = e.clipboardData?.getData("text/plain");
    handleEmailChange(email);
  };

  const handleEmailBlur = (
    e: FocusEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const email = e.target.value;
    handleEmailChange(email);
  };

  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">
        {translate("resources.contacts.field_categories.personal_info")}
      </h6>
      <ArrayInput source="email_jsonb" helperText={false}>
        <SimpleFormIterator
          inline
          disableReordering
          disableClear
          className="[&>ul>li]:border-b-0 [&>ul>li]:pb-0"
        >
          <TextInput
            source="email"
            className="w-full"
            helperText={false}
            label={false}
            placeholder={translate("resources.contacts.fields.email")}
            validate={email()}
            onPaste={handleEmailPaste}
            onBlur={handleEmailBlur}
          />
          <SelectInput
            source="type"
            helperText={false}
            label={false}
            optionText="name"
            choices={personalInfoTypes}
            defaultValue="Work"
            className="w-24 min-w-24"
          />
        </SimpleFormIterator>
      </ArrayInput>
      <ArrayInput source="phone_jsonb" helperText={false}>
        <SimpleFormIterator
          inline
          disableReordering
          disableClear
          className="[&>ul>li]:border-b-0 [&>ul>li]:pb-0"
        >
          <TextInput
            source="number"
            className="w-full"
            helperText={false}
            label={false}
            placeholder={translate("resources.contacts.fields.phone_number")}
          />
          <SelectInput
            source="type"
            helperText={false}
            label={false}
            optionText="name"
            choices={personalInfoTypes}
            defaultValue="Work"
            className="w-24 min-w-24"
          />
        </SimpleFormIterator>
      </ArrayInput>
      <TextInput
        source="linkedin_url"
        helperText={false}
        validate={isLinkedinUrl}
      />
    </div>
  );
};

const ContactLocationInputs = () => {
  const translate = useTranslate();
  const confidenceChoices = [
    { id: "high", name: "High" },
    { id: "medium", name: "Medium" },
    { id: "low", name: "Low" },
  ];

  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">
        {translate("resources.contacts.field_categories.location")}
      </h6>
      <TextInput source="city" helperText={false} />
      <TextInput source="region" helperText={false} />
      <TextInput source="country" helperText={false} />
      <TextInput source="location_source" helperText={false} />
      <DateTimeInput source="location_verified_at" helperText={false} />
      <SelectInput
        source="location_confidence"
        choices={confidenceChoices}
        helperText={false}
        optionText="name"
        optionValue="id"
      />
    </div>
  );
};

const ContactMiscInputs = () => {
  const translate = useTranslate();
  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">
        {translate("resources.contacts.field_categories.misc")}
      </h6>
      <TextInput source="background" multiline helperText={false} />
      <BooleanInput source="has_newsletter" helperText={false} />
    </div>
  );
};

const ContactRelationshipInputs = () => {
  const translate = useTranslate();
  // From the generated vocabulary, so the form offers exactly what the triage
  // deck writes — including "None", which the hand-written list predated.
  const relationshipStrengthChoices = RELATIONSHIP_LEVELS.map((term) => ({
    id: term.value,
    name: term.label,
  }));
  // Sourced from the generated vocabulary rather than retyped: the database
  // trigger rejects anything outside the catalog, so a hand-written choice
  // list here would surface as a save error instead of a validation message.
  const choicesFrom = (terms: readonly { value: string; label: string }[]) =>
    terms.map((term) => ({ id: term.value, name: term.label }));
  const fitQualityChoices = choicesFrom(FIT_QUALITIES);
  const icpProfileChoices = choicesFrom(ICP_PROFILES);
  const relationshipTypeChoices = choicesFrom(RELATIONSHIP_TYPES);
  const buyerStatusChoices = choicesFrom(BUYER_STATUSES);
  const fitConfidenceChoices = choicesFrom(FIT_CONFIDENCES);
  const nextRelationshipMoveChoices = [
    { id: "Comment", name: "Comment" },
    { id: "Connect", name: "Connect" },
    { id: "DM", name: "DM" },
    { id: "Email", name: "Email" },
    { id: "Useful Resource", name: "Useful Resource" },
    { id: "Intro Ask", name: "Intro Ask" },
    { id: "Referral Ask", name: "Referral Ask" },
    { id: "Podcast Invite", name: "Podcast Invite" },
    { id: "Meeting Prep", name: "Meeting Prep" },
    { id: "Wait", name: "Wait" },
    { id: "Disqualify", name: "Disqualify" },
  ];
  // Feeds source="relationship_motion" despite the "Relationship Type" label.
  // Named for the column, not the label, now that relationship_types exists.
  const relationshipMotionChoices = [
    { id: "Customer", name: "Customer" },
    { id: "Ecosystem", name: "Ecosystem" },
    { id: "Partner", name: "Partner" },
    { id: "Prospect", name: "Prospect" },
    { id: "Referral Source", name: "Referral Source" },
    { id: "Student", name: "Student" },
    { id: "Vendor", name: "Vendor" },
    { id: "Other", name: "Other" },
  ];

  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">
        {translate("resources.contacts.field_categories.relationship")}
      </h6>
      <SelectInput
        source="status"
        label="Status"
        choices={relationshipStrengthChoices}
        helperText={false}
        optionText="name"
        optionValue="id"
        defaultValue="C"
      />
      {/*
        Two orthogonal axes. `fit_quality` is how strong this record is at
        whatever role it plays; `relationship_types` is which roles those are.
        Keeping them apart is what lets a delivery partner who will never buy
        be recorded honestly, which the single-valued pair before it could not.
      */}
      <SelectInput
        source="fit_quality"
        label="Fit quality"
        choices={fitQualityChoices}
        helperText="How strong at whatever role it plays — not how good a buyer."
        optionText="name"
        optionValue="id"
      />
      <AutocompleteArrayInput
        source="relationship_types"
        label="Relationship types"
        choices={relationshipTypeChoices}
        helperText="More than one is normal: a delivery partner is often a referral partner too."
        optionText="name"
        optionValue="id"
      />
      <SelectInput
        source="icp_profile"
        label="ICP profile"
        choices={icpProfileChoices}
        helperText="Which buyer profile, when the relationship includes 'client'."
        optionText="name"
        optionValue="id"
      />
      {/*
        Replaces the old free-text `influence` select. buyer_proximity is
        derived from this and fit_quality by the database and is read-only
        everywhere, so there is nothing to edit for it here.
      */}
      <SelectInput
        source="buyer_status"
        label="Buyer status"
        choices={buyerStatusChoices}
        helperText="Where they sit relative to a buying decision in their own org."
        optionText="name"
        optionValue="id"
      />
      <SelectInput
        source="fit_confidence"
        label="Fit confidence"
        choices={fitConfidenceChoices}
        helperText="Evidence quality, not enthusiasm."
        optionText="name"
        optionValue="id"
      />
      <TextInput
        source="fit_rationale"
        label="Fit rationale"
        helperText="The decisive facts only — not a restatement of the profile."
        multiline
      />
      <TextInput
        source="fit_hook"
        label="Hook"
        helperText="Why this matters now. 'Interested in AI' is not a hook."
        multiline
      />
      <TextInput
        source="fit_missing_evidence"
        label="Missing evidence"
        helperText="The single fact most likely to change the judgement."
        multiline
      />
      <SelectInput
        source="relationship_motion"
        label="Relationship Type"
        choices={relationshipMotionChoices}
        helperText={false}
        optionText="name"
        optionValue="id"
      />
      <SelectInput
        source="next_relationship_move"
        choices={nextRelationshipMoveChoices}
        helperText={false}
        optionText="name"
        optionValue="id"
      />
      <DateTimeInput
        source="last_meaningful_interaction_at"
        helperText={false}
      />
      <TextInput source="relationship_evidence" multiline helperText={false} />
    </div>
  );
};

export const ContactStatusSelector = () => {
  const record = useRecordContext<Contact>();
  const [update] = useUpdate<Contact>();
  const notify = useNotify();
  if (!record) return null;

  const handleStatusChange = (nextStatus: string) => {
    if (nextStatus === record?.status) return;

    update(
      "contacts",
      {
        id: record.id,
        data: { status: nextStatus },
        previousData: record,
      },
      {
        mutationMode: "optimistic",
        onError: (error) => {
          notify(
            typeof error === "string"
              ? error
              : error?.message || "ra.notification.http_error",
            {
              type: "error",
              messageArgs: {
                _: typeof error === "string" ? error : error?.message,
              },
            },
          );
        },
      },
    );
  };

  return (
    <div className="[&_button]:w-auto">
      <StatusSelector
        status={record?.status}
        setStatus={handleStatusChange}
        triggerClassName="w-full"
      />
    </div>
  );
};
